/**
 * Logout hygiene: asks the service worker that controls this page to delete the
 * runtime caches holding the signed-in session's API reads, live readings and map
 * tiles (src/sw/routes.ts LOGOUT_CLEARED_CACHES). The precache (shell, bundles,
 * /data) stays so the app still boots offline; docs/SECURITY.md has the reasoning.
 */
import { SW_CLEAR_RUNTIME, isSwClearRuntimeReply } from '@/sw/protocol';

/** Longest the logout waits for the worker's answer before it goes on to /login. */
export const CLEAR_TIMEOUT_MS = 3000;

/** The part of ServiceWorkerContainer this needs; tests pass a fake. */
export interface ControllerHost {
  controller: Pick<ServiceWorker, 'postMessage'> | null;
}

function defaultHost(): ControllerHost | null {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null;
}

/**
 * Resolves with the names of the caches the worker deleted, or null when no
 * service worker controls the page (the dev server, or before the first install).
 * Rejects when the worker reports a failure or does not answer in time.
 */
export function clearRuntimeCaches(host: ControllerHost | null = defaultHost(), timeoutMs = CLEAR_TIMEOUT_MS): Promise<string[] | null> {
  const controller = host?.controller ?? null;
  if (!controller) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => {
      channel.port1.close();
      reject(new Error(`the service worker did not confirm the cache clear within ${timeoutMs} ms`));
    }, timeoutMs);
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      clearTimeout(timer);
      channel.port1.close();
      const reply = event.data;
      if (!isSwClearRuntimeReply(reply)) reject(new Error(`unexpected answer from the service worker: ${JSON.stringify(reply)}`));
      else if (reply.ok) resolve(reply.cleared);
      else reject(new Error(`the service worker could not clear its caches: ${reply.error}`));
    };
    controller.postMessage({ type: SW_CLEAR_RUNTIME }, [channel.port2]);
  });
}
